
import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { api, getTrialStatus } from '../api';

interface RequireAuthProps {
  children: React.ReactNode;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const location = useLocation();
  const isMounted = useRef(true);
  
  const isSetupPath = location.pathname.startsWith('/admin/setup');
  const [roleCheckLoading, setRoleCheckLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [redirectToUser, setRedirectToUser] = useState(false);
  const [redirectToSetup, setRedirectToSetup] = useState(false);
  const [redirectToSettings, setRedirectToSettings] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    
    const checkRole = async () => {
      // 1. 未ログインの場合
      if (!currentUser) {
        if (isMounted.current) setRoleCheckLoading(false);
        return;
      }

      // 2. セットアップ画面にいる場合は、これ以上のリダイレクトチェックを行わず表示を許可
      if (isSetupPath) {
        if (isMounted.current) {
          setIsAuthorized(true);
          setRoleCheckLoading(false);
        }
        return;
      }

      try {
        const staffList = await api.getStaff();
        const me = staffList.find(s => s.id === currentUser.uid);

        if (isMounted.current) {
          if (me) {
            // 一般スタッフの場合 -> ユーザー画面へ
            if (me.role === 'staff') {
              // スタッフはユーザー画面のみ。既にユーザー画面にいる場合はそのまま表示する。
              if (location.pathname.startsWith('/user')) setIsAuthorized(true);
              else setRedirectToUser(true);
            } 
            // 管理者の場合 -> セットアップ状況を確認
            else {
              const needsSetup = !me.setupCompleted;
              if (needsSetup) {
                setRedirectToSetup(true);
              } else {
                // プランガード: トライアル終了+未課金の場合は設定画面へ誘導
                const isSettingsPath = location.pathname.startsWith('/admin/settings');
                if (!isSettingsPath) {
                  const stores = await api.getStores();
                  const primaryStore = stores.length > 0 ? stores[0] : null;
                  if (primaryStore) {
                    const trial = getTrialStatus(primaryStore);
                    const isPaid = !!primaryStore.stripeSubscriptionId;
                    if (trial.isExpired && !isPaid) {
                      setRedirectToSettings(true);
                    } else {
                      setIsAuthorized(true);
                    }
                  } else {
                    setIsAuthorized(true);
                  }
                } else {
                  setIsAuthorized(true);
                }
              }
            }
          } else {
            // プロファイルがない（新規登録直後）
            setRedirectToSetup(true);
          }
        }
      } catch (e) {
        console.error("[AUTH_GUARD] Error:", e);
        // Firestoreパーミッションエラー等の場合はセットアップへ誘導するが、
        // チュートリアル中の新規ユーザーがステップ1に戻ることを防ぐため
        // isSetupPathの場合はリダイレクトしない
        if (isMounted.current && !isSetupPath) setRedirectToSetup(true);
      } finally {
        if (isMounted.current) setRoleCheckLoading(false);
      }
    };

    if (!authLoading) {
      checkRole();
    }

    return () => { isMounted.current = false; };
  }, [currentUser, authLoading, location.pathname, isSetupPath]);

  // 認証情報の読み込み中
  if (authLoading || roleCheckLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white fixed inset-0 z-50">
        <div className="text-center">
            <Loader2 className="animate-spin text-blue-600 mx-auto mb-4" size={32} />
            <p className="text-slate-400 text-[10px] font-bold tracking-widest uppercase">セッションを初期化中...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (redirectToUser) {
    return <Navigate to="/user/shifts" replace />;
  }

  if (redirectToSettings) {
    return <Navigate to="/admin/settings" replace />;
  }

  if (redirectToSetup && !isSetupPath) {
    return <Navigate to="/admin/setup" replace />;
  }

  // "/admin/setup" 自体は isAuthorized が true になっているのでここを通る
  if (!isAuthorized && !isSetupPath) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
